import React, { useEffect, useState } from "react";
import { Team } from "../../models/teamModel";
import { useTeamsStore } from "../../store/teamsStore";
import { IconButton } from "../IconButton";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useConfirmationDialog } from "../ConfirmDialog";
import {
  classNames,
  getGolfPlayerCategory,
  getUserFullName,
} from "../../utils/appUtils";
import { Text } from "../catalyst/text";
import { Heading } from "../catalyst/heading";
import { Button } from "../catalyst/button";
import { useSeasonsStore } from "../../store/seasonsStore";
import { UserView } from "../UserView";
import { usePlayersStore } from "../../store/playersStore";
import { Badge } from "../catalyst/badge";

interface ShortlistPlayersProps {
  team: Team | null;
}

const ShortlistPlayers: React.FC<ShortlistPlayersProps> = ({ team }) => {
  const { players, getPlayers } = usePlayersStore();
  const { openDialog } = useConfirmationDialog();
  const { showTeams, getTeamDetails, saveShortlistedPlayers } = useTeamsStore();
  const { currentSeason } = useSeasonsStore();

  const [shortListedPlayers, setShortListedPlayers] = useState<any[]>([]);
  const [filteredPlayerInvitees, setFilteredPlayerInvitees] = useState<any>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!team) return;
    getPlayers(team.seasonId);
  }, []);

  useEffect(() => {
    if (!team) return;
    setShortListedPlayers(
      team.shortlistedPlayersColl ? team.shortlistedPlayersColl : []
    );
  }, [team]);

  useEffect(() => {
    if (!team || !players) {
      setFilteredPlayerInvitees([]);
      return;
    }
    setFilteredPlayerInvitees(
      players.filter((player: any) =>
        !!player.playerHandicap &&
        getUserFullName(player).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search, players]);

  const saveListOnChange = async (newList: any[]) => {
    if (!team) return;

    const shortListedColl = newList.map((p, i) => ({
      playerId: p.playerId,
      order: i + 1,
    }));
    await saveShortlistedPlayers(team.id, shortListedColl);
    await getTeamDetails(team.id);
  };

  const saveShortlist = () => {
    openDialog({
      title: `Save shortlisted players`,
      message: `Are you sure you want to save the updated shortlist?`,
      onConfirm: async () => {
        if (!team) return;

        const shortListedColl = shortListedPlayers.map((p, i) => ({
          playerId: p.playerId,
          order: i + 1,
        }));
        await saveShortlistedPlayers(team.id, shortListedColl);
        await getTeamDetails(team.id);
      },
    });
  };

  const handleAddToShortlist = (player: any) => {
    if (
      shortListedPlayers.some((p) => p.playerId === player.playerId) ||
      shortListedPlayers.length >=
        (currentSeason?.maxShortListedPlayers
          ? currentSeason?.maxShortListedPlayers
          : 0)
    ) {
      return;
    }
    const newShortListedPlayers = [...shortListedPlayers, player];
    setShortListedPlayers(newShortListedPlayers);
    saveListOnChange(newShortListedPlayers);
  };
  const handleRemoveFromShortlist = (player: any) => {
    const newShortListedPlayers = shortListedPlayers.filter(
      (p) => p.playerId !== player.playerId
    );
    setShortListedPlayers(newShortListedPlayers);
    saveListOnChange(newShortListedPlayers);
  };

  const movePlayerUp = (player: any) => {
    const index = shortListedPlayers.findIndex(
      (p) => p.playerId === player.playerId
    );
    if (index <= 0) return;
    const newShortListedPlayers = [...shortListedPlayers];
    newShortListedPlayers[index] = shortListedPlayers[index - 1];
    newShortListedPlayers[index - 1] = player;
    setShortListedPlayers(newShortListedPlayers);
    saveListOnChange(newShortListedPlayers);
  };
  const movePlayerDown = (player: any) => {
    const index = shortListedPlayers.findIndex(
      (p) => p.playerId === player.playerId
    );
    if (index >= shortListedPlayers.length - 1) return;
    const newShortListedPlayers = [...shortListedPlayers];
    newShortListedPlayers[index] = shortListedPlayers[index + 1];
    newShortListedPlayers[index + 1] = player;
    setShortListedPlayers(newShortListedPlayers);
    saveListOnChange(newShortListedPlayers);
  };

  return (
    <>
      <div className="flex justify-end mt-[-23px]">
        <IconButton onClick={() => showTeams()} Icon={XMarkIcon} text="" />
      </div>
      <div className="">
        <Heading className="text-xl font-semibold">
          Shortlist Players for Draft.
        </Heading>

        <div>
          <Text>
            Shortlist players to keep the list handy for the draft. You can
            search for players by name.
          </Text>
          <div className="mt-5">
            <div className="flex flex-row flex-wrap justify-between">
              <Heading className="text-lg font-semibold">
                Shortlisted Players (max: {currentSeason?.maxShortListedPlayers}
                )
              </Heading>
              <Button color="green" onClick={saveShortlist} className="hidden">
                Save Sortlist
              </Button>
            </div>
            <div className="p-1 bg-white rounded-sm dark:bg-gray-800">
              {shortListedPlayers && shortListedPlayers.length === 0 && (
                <Text>No players shortlisted yet.</Text>
              )}
              <ol className="list-decimal list-inside mt-2">
                {shortListedPlayers &&
                  shortListedPlayers.map((player, index) => (
                    <>
                      <ShortlistedPlayerDesktop
                        player={player}
                        index={index}
                        handleRemoveFromShortlist={handleRemoveFromShortlist}
                        movePlayerUp={movePlayerUp}
                        movePlayerDown={movePlayerDown}
                        shortListedPlayers={shortListedPlayers}
                      />
                      <ShortlistedPlayerMobile
                        player={player}
                        index={index}
                        handleRemoveFromShortlist={handleRemoveFromShortlist}
                        movePlayerUp={movePlayerUp}
                        movePlayerDown={movePlayerDown}
                        shortListedPlayers={shortListedPlayers}
                      />
                    </>
                  ))}
              </ol>
            </div>
          </div>
        </div>

        <div className="relative flex flex-1 h-8 mt-5">
          <label htmlFor="search-field" className="sr-only">
            Search
          </label>
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 dark:text-gray-200"
          />
          <input
            id="search-field"
            name="search"
            type="search"
            placeholder={"Search players..."}
            onChange={(e) => setSearch(e.target.value)}
            className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 dark:text-gray-200 dark:placeholder:text-gray-200 bg-white dark:bg-gray-600 placeholder:text-gray-400 focus:ring-0 sm:text-sm rounded-md"
          />
        </div>
        <div
          className="mt-5 overflow-auto bg-white dark:bg-gray-800 shadow rounded mb-5 p-2"
          style={{ height: "calc(100vh - 300px)" }}
        >
          {filteredPlayerInvitees
            .sort((a: any, b: any) => a.playerHandicap - b.playerHandicap)
            .map((player: any, index: number) => (
              <>
                <AvailablePlayerDesktop
                  player={player}
                  index={index}
                  handleAddToShortlist={handleAddToShortlist}
                  shortListedPlayers={shortListedPlayers}
                  currentSeason={currentSeason}
                />
                <AvailablePlayerMobile
                  player={player}
                  index={index}
                  handleAddToShortlist={handleAddToShortlist}
                  shortListedPlayers={shortListedPlayers}
                  currentSeason={currentSeason}
                />
              </>
            ))}

          {filteredPlayerInvitees.length === 0 && (
            <Text className="text-center">No players found to shortlist.</Text>
          )}
          {filteredPlayerInvitees.length === 0 &&
            players &&
            players.length > 0 &&
            search && (
              <Text className="text-center">
                No players found for search term: "{search}"
              </Text>
            )}
        </div>
      </div>
    </>
  );
};

const AvailablePlayerDesktop: React.FC<any> = ({
  player,
  index,
  handleAddToShortlist,
  shortListedPlayers,
  currentSeason,
}) => {
  return (
    <div
      key={player.playerId}
      className={classNames(
        "flex items-center justify-between p-2 rounded mb-2 gap-2",
        index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
        "hidden sm:flex"
      )}
    >
      <UserView member={player} />

      {player.isOwner && (
        <Badge
          color="blue"
          className="text-lg font-semibold mr-2 px-2.5 py-0.5"
        >
          Owner
        </Badge>
      )}
      <div className="w-3 text-center mr-1">
        <Text className="text-md font-semibold">{player.playerHandicap}</Text>
      </div>
      <div className="w-3 text-center mx-2">
        <Text className="text-md font-semibold">
          {getGolfPlayerCategory(player.ageCategory, player.gender)}
        </Text>
      </div>
      <Button
        color="blue"
        className="px-3 py-1 rounded"
        onClick={() => handleAddToShortlist(player)}
        disabled={
          shortListedPlayers.some((p: any) => p.playerId === player.playerId) ||
          shortListedPlayers.length >=
            (currentSeason?.maxShortListedPlayers
              ? currentSeason?.maxShortListedPlayers
              : 0)
        }
      >
        Shortlist
      </Button>
    </div>
  );
};
const AvailablePlayerMobile: React.FC<any> = ({
  player,
  index,
  handleAddToShortlist,
  shortListedPlayers,
  currentSeason,
}) => {
  return (
    <div
      key={player.playerId}
      className={classNames(
        "flex items-center justify-between p-2 rounded mb-2 gap-2",
        index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
        "sm:hidden"
      )}
    >
      <div className="w-full">
        <UserView member={player} />
        <div className="flex gap-2 justify-between items-center">
          <div className="flex gap-3 text-center ml-8">
            {player.isOwner && (
              <Badge
                color="blue"
                className="text-lg font-semibold mr-2 px-2.5 py-0.5"
              >
                Owner
              </Badge>
            )}
            <Text className="text-md font-semibold">
              {player.playerHandicap}
            </Text>
            <Text className="text-md font-semibold">
              {getGolfPlayerCategory(player.ageCategory, player.gender)}
            </Text>
          </div>
          <Button
            color="blue"
            className="h-8"
            onClick={() => handleAddToShortlist(player)}
            disabled={
              shortListedPlayers.some(
                (p: any) => p.playerId === player.playerId
              ) ||
              shortListedPlayers.length >=
                (currentSeason?.maxShortListedPlayers
                  ? currentSeason?.maxShortListedPlayers
                  : 0)
            }
          >
            Shortlist
          </Button>
        </div>
      </div>
    </div>
  );
};

const ShortlistedPlayerDesktop: React.FC<any> = ({
  player,
  index,
  handleRemoveFromShortlist,
  movePlayerUp,
  movePlayerDown,
  shortListedPlayers,
}) => {
  return (
    <div
      key={player.playerId}
      className={classNames(
        "mt-1 flex gap-2 items-center p-2 rounded",
        index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
        "hidden sm:flex"
      )}
    >
      <Text className="text-sm font-semibold">{index + 1}.</Text>
      <UserView member={player} />
      <div className="w-2 text-center">
        <Text className="text-md font-semibold">{player.playerHandicap}</Text>
      </div>
      <div className="w-3 text-center mx-2">
        <Text className="text-md font-semibold">
          {getGolfPlayerCategory(player.ageCategory, player.gender)}
        </Text>
      </div>
      {index > 0 && (
        <Button
          outline
          className="px-3 py-1 rounded"
          onClick={() => movePlayerUp(player)}
        >
          <ArrowUpIcon className="h-4 w-4 mr-1" />
        </Button>
      )}
      {index < shortListedPlayers.length - 1 && (
        <Button
          outline
          className="px-3 py-1 rounded"
          onClick={() => movePlayerDown(player)}
        >
          <ArrowDownIcon className="h-4 w-4 mr-1" />
        </Button>
      )}

      <Button
        color="red"
        className="px-3 py-1 rounded"
        onClick={() => handleRemoveFromShortlist(player)}
      >
        <TrashIcon className="h-4 w-4 mr-1" />
      </Button>
    </div>
  );
};
const ShortlistedPlayerMobile: React.FC<any> = ({
  player,
  index,
  handleRemoveFromShortlist,
  movePlayerUp,
  movePlayerDown,
  shortListedPlayers,
}) => {
  return (
    <div
      key={player.playerId}
      className={classNames(
        "flex flex-column mt-1 gap-2 items-center p-2 rounded",
        index % 2 === 0 ? "bg-gray-100 dark:bg-gray-700" : "",
        "sm:hidden"
      )}
    >
      <div className="w-full">
        <div className="flex gap-3 items-center">
          <Text className="text-sm font-semibold">{index + 1}.</Text>
          <UserView member={player} />
        </div>
        <div className="flex gap-2 justify-between items-center">
          <div className="w-2 text-center gap-3 flex">
            <Text className="text-md font-semibold">
              {player.playerHandicap}
            </Text>
            <Text className="text-md font-semibold">
              {getGolfPlayerCategory(player.ageCategory, player.gender)}
            </Text>
          </div>

          <div className="flex gap-2 justify-between items-center">
            {index > 0 && (
              <Button
                outline
                className="px-3 py-1 rounded h-7"
                onClick={() => movePlayerUp(player)}
              >
                <ArrowUpIcon className="h-4 w-4 mr-1" />
              </Button>
            )}
            {index < shortListedPlayers.length - 1 && (
              <Button
                outline
                className="px-3 py-1 rounded h-7"
                onClick={() => movePlayerDown(player)}
              >
                <ArrowDownIcon className="h-4 w-4 mr-1" />
              </Button>
            )}
            <Button
              color="red"
              className="px-3 py-1 rounded h-7"
              onClick={() => handleRemoveFromShortlist(player)}
            >
              <TrashIcon className="h-4 w-4 mr-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortlistPlayers;
